import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Simple, dependency-free tooltip that stays within the viewport.
 * - Uses a portal (body) and `position: fixed` for reliable boundary checks.
 * - Supports hover + focus and closes on Escape / scroll.
 */
const Tooltip = ({
  content,
  children,
  className = "",
  tooltipClassName = "",
  placement = "bottom",
  align = "start",
  offset = 8,
  viewportPadding = 8,
  disabled = false,
}) => {
  const triggerRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, ready: false });

  const tooltipId = useMemo(
    () => `tooltip-${Math.random().toString(16).slice(2)}`,
    []
  );

  const close = () => setOpen(false);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Close on scroll (any scroll container).
  useEffect(() => {
    if (!open) return;
    const onScroll = () => close();
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [open]);

  // Position tooltip within viewport.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;

    const compute = () => {
      const t = trigger.getBoundingClientRect();
      // Ensure tooltip has its final size (after portal render).
      const w = tip.offsetWidth;
      const h = tip.offsetHeight;

      const vpW = window.innerWidth;
      const vpH = window.innerHeight;

      // Base position by placement.
      let top = placement === "top" ? t.top - offset - h : t.bottom + offset;

      // Flip if it would overflow vertically.
      if (top + h > vpH - viewportPadding && t.top - offset - h >= viewportPadding) {
        top = t.top - offset - h;
      } else if (top < viewportPadding && t.bottom + offset + h <= vpH - viewportPadding) {
        top = t.bottom + offset;
      }

      // Horizontal alignment.
      let left = t.left;
      if (align === "center") left = t.left + t.width / 2 - w / 2;
      if (align === "end") left = t.right - w;

      // Shift inside viewport.
      left = clamp(left, viewportPadding, vpW - viewportPadding - w);
      top = clamp(top, viewportPadding, vpH - viewportPadding - h);

      setPos({ left, top, ready: true });
    };

    // Compute now and on resize.
    const raf = window.requestAnimationFrame(compute);
    window.addEventListener("resize", compute);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", compute);
    };
  }, [open, placement, align, offset, viewportPadding]);

  const child = React.Children.only(children);
  const childProps = child.props || {};

  const triggerProps = disabled
    ? {}
    : {
        ref: triggerRef,
        onMouseEnter: (e) => {
          childProps.onMouseEnter?.(e);
          setOpen(true);
        },
        onMouseLeave: (e) => {
          childProps.onMouseLeave?.(e);
          close();
        },
        onFocus: (e) => {
          childProps.onFocus?.(e);
          setOpen(true);
        },
        onBlur: (e) => {
          childProps.onBlur?.(e);
          close();
        },
        "aria-describedby": open ? tooltipId : undefined,
      };

  return (
    <>
      {React.cloneElement(child, {
        ...triggerProps,
        className: [childProps.className, className].filter(Boolean).join(" "),
      })}

      {open &&
        !disabled &&
        createPortal(
          <div
            ref={tipRef}
            id={tooltipId}
            role="tooltip"
            className={[
              "fixed z-[9999] max-w-[320px] bg-white-f p-3 rounded-md border border-grey-20 shadow-md text-xs text-black-0",
              tooltipClassName,
            ]
              .filter(Boolean)
              .join(" ")}
            style={{
              left: pos.left,
              top: pos.top,
              opacity: pos.ready ? 1 : 0,
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;

